import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, Search } from 'lucide-react'
import clsx from 'clsx'

/**
 * Náhrada nativního <select>: stejné API (value, onChange s e.target.value, <option> jako děti),
 * ale s fulltextovým hledáním a seznamem vykresleným do portálu, takže ho neořízne žádný
 * rodičovský kontejner s overflow. Používá se všude místo <select>.
 */

export interface PopoverPosition { top: number; left: number; width: number; maxHeight: number; up: boolean }

/** Spočítá pozici vyskakovacího seznamu pod (nebo nad) prvkem; seznam je fixed a nikdy neořízne rodič. */
export function computePopoverPosition(anchor: HTMLElement, minWidth = 200, desiredHeight = 320): PopoverPosition {
  const r = anchor.getBoundingClientRect()
  const gap = 4
  const spaceBelow = window.innerHeight - r.bottom - gap - 8
  const spaceAbove = r.top - gap - 8
  const up = spaceBelow < Math.min(desiredHeight, 180) && spaceAbove > spaceBelow
  const maxHeight = Math.max(120, Math.min(desiredHeight, up ? spaceAbove : spaceBelow))
  const width = Math.max(r.width, minWidth)
  const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - width - 8))
  return { top: up ? r.top - gap - maxHeight : r.bottom + gap, left, width, maxHeight, up }
}

/** Sleduje pozici kotvy (scroll/resize) a vrací souřadnice pro fixed popover. */
export function usePopoverPosition(anchorRef: React.RefObject<HTMLElement>, open: boolean, minWidth = 200): PopoverPosition | null {
  const [pos, setPos] = useState<PopoverPosition | null>(null)
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) { setPos(null); return }
    const update = () => { if (anchorRef.current) setPos(computePopoverPosition(anchorRef.current, minWidth)) }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update) }
  }, [open, anchorRef, minWidth])
  return pos
}

export interface SelectOptionItem { value: string; label: string; disabled?: boolean; hint?: string }

interface SelectProps {
  value?: string | number | null
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  children?: React.ReactNode
  /** Alternativa k dětem <option>. */
  options?: SelectOptionItem[]
  className?: string
  disabled?: boolean
  placeholder?: string
  name?: string
  id?: string
  title?: string
  /** Skrýt vyhledávací pole (u velmi krátkých seznamů). Výchozí: hledání vždy. */
  searchable?: boolean
  /** Povinné pole – validuje se přes skrytý input, stejně jako u nativního selectu ve formuláři. */
  required?: boolean
  'aria-label'?: string
}

function optionText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(optionText).join('')
  if (React.isValidElement(node)) return optionText((node.props as { children?: React.ReactNode }).children)
  return ''
}

function collectOptions(children: React.ReactNode, out: SelectOptionItem[]): void {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === 'option') {
      const p = child.props as { value?: string | number; disabled?: boolean; children?: React.ReactNode }
      const label = optionText(p.children).trim()
      out.push({ value: p.value !== undefined ? String(p.value) : label, label, disabled: p.disabled })
      return
    }
    // optgroup, fragmenty, pole
    collectOptions((child.props as { children?: React.ReactNode }).children, out)
  })
}

export default function Select({
  value, onChange, children, options, className, disabled, placeholder, name, id, title, searchable = true, required, ...rest
}: SelectProps) {
  const items = useMemo(() => {
    if (options) return options
    const out: SelectOptionItem[] = []
    collectOptions(children, out)
    return out
  }, [children, options])

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const pos = usePopoverPosition(triggerRef, open)

  const strValue = value === null || value === undefined ? '' : String(value)
  const selected = items.find(o => o.value === strValue)

  const q = query.trim().toLowerCase()
  const filtered = q ? items.filter(o => o.label.toLowerCase().includes(q) || (o.hint ?? '').toLowerCase().includes(q)) : items

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(Math.max(0, items.findIndex(o => o.value === strValue)))
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDown) }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0) }, [q])

  const pick = (o: SelectOptionItem) => {
    if (o.disabled) return
    setOpen(false)
    if (o.value !== strValue && onChange) {
      // Konzumenti používají e.target.value jako u nativního selectu.
      const target = { value: o.value, name: name ?? '', id: id ?? '' }
      onChange({ target, currentTarget: target, type: 'change', preventDefault() {}, stopPropagation() {} } as unknown as React.ChangeEvent<HTMLSelectElement>)
    }
    triggerRef.current?.focus()
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(filtered.length - 1, a + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)) }
    if (e.key === 'Enter') { e.preventDefault(); const o = filtered[active]; if (o) pick(o) }
  }

  useEffect(() => {
    if (!open || !panelRef.current) return
    const el = panelRef.current.querySelector<HTMLElement>(`[data-idx="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        title={title}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={rest['aria-label']}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={(e) => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true) } }}
        className={clsx(
          'inline-flex items-center justify-between gap-2 text-left bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm shadow-sm',
          'focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25 disabled:opacity-50 disabled:cursor-not-allowed',
          !selected && 'text-ink-500',
          className,
        )}
      >
        <span className="truncate">{selected ? selected.label : (placeholder ?? items[0]?.label ?? '')}</span>
        <ChevronDown size={16} className="flex-shrink-0 text-ink-400" />
      </button>
      {required && (
        <input tabIndex={-1} required name={name} value={strValue} onChange={() => {}} aria-hidden className="sr-only" style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
      )}
      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          onKeyDown={onKey}
          className="fixed z-[1000] bg-white border border-slate-200 rounded-xl shadow-soft-lg flex flex-col overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
        >
          {searchable && (
            <div className="relative border-b border-gray-100 flex-shrink-0">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Hledat…"
                className="w-full pl-8 pr-2 py-2.5 text-sm outline-none"
              />
            </div>
          )}
          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">Nenalezeno</div>
            ) : filtered.map((o, i) => (
              <button
                type="button"
                key={o.value + '|' + i}
                data-idx={i}
                role="option"
                aria-selected={o.value === strValue}
                disabled={o.disabled}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o)}
                className={clsx(
                  'w-full text-left px-3 py-2 min-h-[40px] text-sm flex items-center justify-between gap-2',
                  o.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                  i === active && !o.disabled && 'bg-slate-100',
                  o.value === strValue && 'font-semibold text-brand-700',
                )}
              >
                <span className="truncate">{o.label}{o.hint && <span className="ml-2 text-xs text-gray-400">{o.hint}</span>}</span>
                {o.value === strValue && <Check size={14} className="flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
